/**
 * 🔨 Duplicate detection is tiered, strongest signal first (piece 10). The
 * system never auto-merges and never silently blocks: the modal shows both
 * records, reports why it matched, and offers exactly three actions.
 */
import { comparablePhone, emailDomain, normaliseAddress, normaliseCompanyName, normaliseText, normaliseTypedAddress, websiteDomain, type TypedAddress } from './normalise';

/** The Company tiers, in the order they run. */
export const COMPANY_TIERS = ['name', 'domain', 'phone', 'address'] as const;
export type CompanyTier = (typeof COMPANY_TIERS)[number];

/**
 * The Location tier: the normalised typed address, folded so St and Street are
 * one street (piece 13). 💰 A second tier matched on `verifiedByProviderKey`,
 * which holds the name of the provider that answered — `openstreetmap`,
 * `google`, `fake` — and never a key for one building, so every building one
 * provider had verified matched every other (found in review, 2026-09-15).
 * 🔨 No tier compares a place until a column holds one.
 */
export const LOCATION_TIERS = ['address'] as const;
export type LocationTier = (typeof LOCATION_TIERS)[number];

/**
 * The Person tiers, in the order they run (decision 2210, amending 0042): the
 * name first at any company, then email exact, then phone. A pair's tier is
 * the first of these its signals hold.
 */
export const PERSON_TIERS = ['name', 'email', 'phone'] as const;
export type PersonTier = (typeof PERSON_TIERS)[number];

/**
 * 🔒 The signals a person pair is read on, strongest first (decision 2210). The
 * name, email and phone find a pair; the company, the work-site address and a
 * creation within a day of each other only strengthen one already found.
 */
export const PERSON_SIGNALS = ['name', 'email', 'phone', 'company', 'address', 'createdNear'] as const;
export type PersonSignal = (typeof PERSON_SIGNALS)[number];

/** The signals that can disagree; a creation date far apart is never held against a pair. */
const DISAGREEING_SIGNALS: readonly PersonSignal[] = ['email', 'phone', 'company', 'address'];

/** How a signal reads in a reason sentence, agreeing and disagreeing. */
const SIGNAL_AGREES: Readonly<Record<PersonSignal, string>> = {
  name: 'same name', email: 'same email', phone: 'same phone', company: 'same company', address: 'same address', createdNear: 'created within a day of each other',
};
const SIGNAL_DISAGREES: Readonly<Partial<Record<PersonSignal, string>>> = {
  email: 'different emails', phone: 'different phones', company: 'different companies', address: 'different addresses',
};

/** One day, the span inside which two records read as created together. */
const CREATED_NEAR_MS = 24 * 60 * 60 * 1000;

/** The three actions the modal offers, and no fourth. */
export const DUPLICATE_ACTIONS = ['use-existing', 'create-anyway', 'cancel'] as const;
export type DuplicateAction = (typeof DUPLICATE_ACTIONS)[number];

/**
 * 🔨 On a Person the other-person answer is the effortless one and the
 * same-person answer the considered one (decision 0042), and neither is a
 * default: a missed collision creates a duplicate, which is the cheap failure.
 */
export const EFFORTLESS_PERSON_ANSWER: DuplicateAction = 'create-anyway';
export const DUPLICATE_DEFAULT_ACTION: DuplicateAction | null = null;

export interface CompanyCandidate {
  id: string;
  displayName: string;
  website?: string | null;
  mainPhoneNumber?: string | null;
  emails?: readonly string[];
  addressParts?: readonly (string | null | undefined)[];
}

export interface PersonCandidate {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string | null;
  /** The addresses on this person's employment links, which is where an address lives. */
  emails?: readonly string[];
  /** The phones that pair: a Mobile, and a Desk phone that is not a company's number (decision 3163). */
  phones?: readonly string[];
  /** 🔒 The Desk phones that are a company's number (decision 3163): never a signal, and named in the reason only where a pair is listed for another. */
  companyPhones?: readonly string[];
  /** The Companies this person is linked to, shown beside every candidate; on a subject, the company the form names. */
  companyIds?: readonly string[];
  /** The street addresses of this person's work-location sites; a link's residence is confidential and never read (decision 2210). */
  addresses?: readonly TypedAddress[];
  /** When the record was created, as an ISO instant; absent on a record not yet written. */
  createdAt?: string | null;
  lastActiveAt?: string | null;
}

/** A building the Location tier compares, which is the typed address and nothing else. */
export interface LocationCandidate extends TypedAddress {
  id: string;
}

export interface DuplicateMatch<Tier extends string, Candidate> {
  candidate: Candidate;
  tier: Tier;
  /** Why it matched, in the reader's words — never a generic "possible duplicate". */
  reason: string;
  /** A person match's agreeing signals, strongest first (decision 2210); absent on a company or a building. */
  signals?: readonly PersonSignal[];
}

/** Nicknames the loose name tier treats as one name, both directions. */
const NICKNAMES: Readonly<Record<string, readonly string[]>> = {
  robert: ['bob', 'rob', 'bobby'], william: ['bill', 'will', 'billy'], richard: ['rick', 'dick', 'rich'],
  james: ['jim', 'jimmy'], john: ['jack', 'johnny'], michael: ['mike', 'mickey'], joseph: ['joe', 'joey'],
  charles: ['charlie', 'chuck'], thomas: ['tom', 'tommy'], daniel: ['dan', 'danny'], matthew: ['matt'],
  christopher: ['chris'], anthony: ['tony'], elizabeth: ['liz', 'beth', 'betty'], katherine: ['kate', 'kathy', 'katie'],
  margaret: ['maggie', 'peggy'], patricia: ['pat', 'patty', 'trish'], jennifer: ['jen', 'jenny'],
  deborah: ['deb', 'debbie'], susan: ['sue', 'susie'], stephen: ['steve'], edward: ['ed', 'eddie', 'ted'],
  andrew: ['andy', 'drew'], nicholas: ['nick'], benjamin: ['ben'], alexander: ['alex'], samuel: ['sam'],
};

const NICKNAME_INDEX: Map<string, string> = (() => {
  const index = new Map<string, string>();
  for (const [formal, shorts] of Object.entries(NICKNAMES)) {
    index.set(formal, formal);
    for (const short of shorts) index.set(short, formal);
  }
  return index;
})();

/** A given name reduced to its formal form, so Bob and Robert compare equal. */
export function canonicalGivenName(value: string): string {
  const normalised = normaliseText(value);
  return NICKNAME_INDEX.get(normalised) ?? normalised;
}

function anyShared(a: readonly string[] | undefined, b: readonly string[] | undefined, map: (v: string) => string | null): string | null {
  if (!a || !b) return null;
  const left = new Set(a.map(map).filter((v): v is string => Boolean(v)));
  for (const value of b) {
    const mapped = map(value);
    if (mapped && left.has(mapped)) return mapped;
  }
  return null;
}

/**
 * The Company check: normalised name, then email or website domain, then phone,
 * then address. Each candidate is reported once, at its strongest tier.
 */
export function findCompanyDuplicates(subject: CompanyCandidate, candidates: readonly CompanyCandidate[]): DuplicateMatch<CompanyTier, CompanyCandidate>[] {
  const subjectName = normaliseCompanyName(subject.displayName);
  const subjectDomains = [
    ...(subject.website ? [websiteDomain(subject.website)] : []),
    ...(subject.emails ?? []).map(emailDomain),
  ].filter((d): d is string => Boolean(d));
  const subjectPhone = subject.mainPhoneNumber ? comparablePhone(subject.mainPhoneNumber) : '';
  const subjectAddress = subject.addressParts ? normaliseAddress(subject.addressParts) : '';

  const matches: DuplicateMatch<CompanyTier, CompanyCandidate>[] = [];
  for (const candidate of candidates) {
    if (candidate.id === subject.id) continue;
    if (subjectName && normaliseCompanyName(candidate.displayName) === subjectName) {
      matches.push({ candidate, tier: 'name', reason: `same name once Inc, LLC and punctuation are set aside: ${candidate.displayName}` });
      continue;
    }
    const candidateDomains = [
      ...(candidate.website ? [websiteDomain(candidate.website)] : []),
      ...(candidate.emails ?? []).map(emailDomain),
    ].filter((d): d is string => Boolean(d));
    const domain = anyShared(subjectDomains, candidateDomains, (v) => v);
    if (domain) {
      matches.push({ candidate, tier: 'domain', reason: `same email or website domain, ${domain}` });
      continue;
    }
    if (subjectPhone && candidate.mainPhoneNumber && comparablePhone(candidate.mainPhoneNumber) === subjectPhone) {
      matches.push({ candidate, tier: 'phone', reason: 'same main phone number' });
      continue;
    }
    if (subjectAddress && candidate.addressParts && normaliseAddress(candidate.addressParts) === subjectAddress) {
      matches.push({ candidate, tier: 'address', reason: 'same address' });
    }
  }
  return matches.sort((a, b) => COMPANY_TIERS.indexOf(a.tier) - COMPANY_TIERS.indexOf(b.tier));
}

/** A given name's forms: as written and its formal form, so Bob, Robert and Rob all meet. */
function givenForms(person: PersonCandidate): string[] {
  const written = [person.firstName, ...(person.nickname ? [person.nickname] : [])].map((v) => normaliseText(v)).filter(Boolean);
  return [...new Set([...written, ...written.map(canonicalGivenName)])];
}

/**
 * The first letters of a person's given forms, the bucket a name pair can only be found in (T1545): two people
 * `personNamesMatch` share a family name and, either by a shared form or by an initial, the first letter of one given form.
 */
export function givenNameInitials(person: Pick<PersonCandidate, 'firstName' | 'nickname'>): string[] {
  return [...new Set(givenForms(person as PersonCandidate).map((form) => form.charAt(0)).filter(Boolean))];
}

/**
 * 🔒 The loose name match (decision 2210): the family name folded, the given
 * name folded and nickname-aware, and a bare initial matching the given name it
 * starts, so J. Smith is John Smith. 🔨 It is deliberately loose: a bare initial
 * matches every given name starting with that letter, nicknames included, and the
 * report puts the pair to the finder rather than deciding it.
 */
export function personNamesMatch(a: PersonCandidate, b: PersonCandidate): boolean {
  const family = normaliseText(a.lastName);
  if (!family || family !== normaliseText(b.lastName)) return false;
  const left = givenForms(a);
  const right = givenForms(b);
  if (left.some((g) => g.length > 1 && right.includes(g))) return true;
  const initialOf = (forms: readonly string[], others: readonly string[]) => forms.some((g) => g.length === 1 && others.some((o) => o.startsWith(g)));
  return initialOf(left, right) || initialOf(right, left);
}

/** Two sides' values compared: agree on any shared, disagree where both carry some and none is shared, and say nothing where either carries none. */
function compareSets(a: readonly string[], b: readonly string[]): 'agree' | 'disagree' | null {
  const left = new Set(a.filter(Boolean));
  const right = b.filter(Boolean);
  if (left.size === 0 || right.length === 0) return null;
  return right.some((v) => left.has(v)) ? 'agree' : 'disagree';
}

const streetAddress = (address: TypedAddress) => ((address.addressLines ?? []).some((line) => line.trim() !== '') ? normaliseTypedAddress(address) : '');

/** One phone a person holds on an employment, as the company-number read takes it (decision 3163). */
export interface PhoneHolding {
  personId: string;
  companyId: string;
  /** The contact method's kind: only a Desk phone can be a company's number. */
  kind: string;
  value: string;
}

/** The key a company's number is held under: the company and the phone's national number. */
export function companyPhoneKey(companyId: string, value: string): string {
  return `${companyId}|${comparablePhone(value)}`;
}

/**
 * 🔒 The company numbers among the Desk phones (decision 3163; T2243): a Desk
 * phone equal to its company's main phone, or one two or more people at that
 * company hold. A Mobile is never one. Answered as `companyPhoneKey` keys.
 */
export function companyPhoneKeys(holdings: readonly PhoneHolding[], mainPhones: ReadonlyMap<string, string | null | undefined>): Set<string> {
  const holders = new Map<string, Set<string>>();
  const keys = new Set<string>();
  for (const holding of holdings) {
    if (holding.kind !== 'Desk' || !comparablePhone(holding.value)) continue;
    const key = companyPhoneKey(holding.companyId, holding.value);
    const main = mainPhones.get(holding.companyId);
    if (main && comparablePhone(main) === comparablePhone(holding.value)) keys.add(key);
    const people = holders.get(key) ?? new Set<string>();
    people.add(holding.personId);
    holders.set(key, people);
    if (people.size >= 2) keys.add(key);
  }
  return keys;
}

/**
 * 🔒 Every company number the two duplicate readers pass over (decision 3163; T2267, T2335): the keys
 * `companyPhoneKeys` answers for the lines in force (`open`), and the company's main phone for every Desk
 * line ever held (`held`), ended ones included, because the main phone stays the company's whoever holds it.
 * One helper for the Duplicates report and the Add Person check, so the two cannot drift again (T2324).
 */
export function companyNumberKeys(
  open: readonly PhoneHolding[],
  held: readonly PhoneHolding[],
  mainPhones: ReadonlyMap<string, string | null | undefined>,
): Set<string> {
  const keys = companyPhoneKeys(open, mainPhones);
  for (const one of held) {
    const main = mainPhones.get(one.companyId);
    if (one.kind === 'Desk' && main && comparablePhone(one.value) && comparablePhone(main) === comparablePhone(one.value)) keys.add(companyPhoneKey(one.companyId, one.value));
  }
  return keys;
}

/** The signals that find a pair by themselves: the company signal counts only beside one (decision 3163). */
const PAIRING_SIGNALS: readonly PersonSignal[] = ['name', 'email', 'phone'];

/**
 * Every signal two people agree on, strongest first, and the strongest they disagree on.
 * 🔒 Decision 3163: a company's number is no phone signal; the company counts only beside a name, an email or a
 * phone that pairs; and a creation within a day never adds to a pair with no name. `companyPhone` says the two
 * share a company's number where no phone of theirs pairs, which the reason names as not counted.
 */
export function personSignals(a: PersonCandidate, b: PersonCandidate, tiers: readonly PersonTier[] = PERSON_TIERS): { agree: PersonSignal[]; disagree: PersonSignal | null; companyPhone?: boolean } {
  const verdicts: Partial<Record<PersonSignal, 'agree' | 'disagree' | null>> = {
    name: tiers.includes('name') && personNamesMatch(a, b) ? 'agree' : null,
    email: tiers.includes('email') ? compareSets((a.emails ?? []).map((v) => v.trim().toLowerCase()), (b.emails ?? []).map((v) => v.trim().toLowerCase())) : null,
    phone: tiers.includes('phone') ? compareSets((a.phones ?? []).map(comparablePhone), (b.phones ?? []).map(comparablePhone)) : null,
    company: compareSets(a.companyIds ?? [], b.companyIds ?? []),
    address: compareSets((a.addresses ?? []).map(streetAddress), (b.addresses ?? []).map(streetAddress)),
  };
  if (a.createdAt && b.createdAt) {
    const apart = Math.abs(new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    verdicts.createdNear = Number.isFinite(apart) && apart <= CREATED_NEAR_MS ? 'agree' : null;
  }
  const found = PERSON_SIGNALS.filter((signal) => verdicts[signal] === 'agree');
  const pairs = found.some((signal) => PAIRING_SIGNALS.includes(signal));
  const agree = found.filter((signal) => (signal !== 'company' || pairs) && (signal !== 'createdNear' || found.includes('name')));
  const disagree = DISAGREEING_SIGNALS.find((signal) => verdicts[signal] === 'disagree') ?? null;
  const companyPhone = tiers.includes('phone') && verdicts.phone !== 'agree'
    && compareSets((a.companyPhones ?? []).map(comparablePhone), (b.companyPhones ?? []).map(comparablePhone)) === 'agree';
  return companyPhone ? { agree, disagree, companyPhone } : { agree, disagree };
}

/** How a company's number two people share reads in a reason (decision 3163). */
const COMPANY_PHONE_NOTE = 'same company phone (not counted)';

/** The reason a person pair is listed: every agreeing signal, then the strongest disagreeing one — *same name, same phone, different companies*. */
export function personReason(agree: readonly PersonSignal[], disagree: PersonSignal | null, companyPhone = false): string {
  return [...agree.map((signal) => SIGNAL_AGREES[signal]), ...(companyPhone ? [COMPANY_PHONE_NOTE] : []), ...(disagree ? [SIGNAL_DISAGREES[disagree]!] : [])].join(', ');
}

/**
 * 🔒 How two person pairs order (decision 2210): a name match first, then the
 * count of agreeing signals, then the strongest of them. Negative puts `a` first.
 */
export function comparePersonSignals(a: readonly PersonSignal[], b: readonly PersonSignal[]): number {
  const named = Number(b.includes('name')) - Number(a.includes('name'));
  if (named !== 0) return named;
  if (a.length !== b.length) return b.length - a.length;
  // Both lists run strongest first, so the first place they differ names the stronger pair.
  for (let i = 0; i < a.length; i += 1) {
    const rank = PERSON_SIGNALS.indexOf(a[i]!) - PERSON_SIGNALS.indexOf(b[i]!);
    if (rank !== 0) return rank;
  }
  return 0;
}

/**
 * The Person check (decision 2210). 🔒 A pair is found by name first, at any
 * company, and by an email or a phone shared without a name; the company, the
 * work-site address and a creation within a day only strengthen a pair found.
 * 🔨 The name tier runs on the create-form screen and the email-exact and phone
 * tiers on the employment screen, because only the name tier reads the bare
 * Person: the addresses live on the link.
 */
export function findPersonDuplicates(
  subject: PersonCandidate,
  candidates: readonly PersonCandidate[],
  options: { tiers?: readonly PersonTier[] } = {},
): DuplicateMatch<PersonTier, PersonCandidate>[] {
  const tiers = options.tiers ?? PERSON_TIERS;
  const matches: DuplicateMatch<PersonTier, PersonCandidate>[] = [];
  for (const candidate of candidates) {
    if (candidate.id === subject.id) continue;
    const { agree, disagree, companyPhone } = personSignals(subject, candidate, tiers);
    const tier = PERSON_TIERS.find((t) => (agree as readonly string[]).includes(t));
    if (!tier) continue;
    matches.push({ candidate, tier, reason: personReason(agree, disagree, companyPhone), signals: agree });
  }
  return matches.sort((a, b) => comparePersonSignals(a.signals ?? [], b.signals ?? []));
}

/** One line of a building, as the modal prints it beside the one being created. */
export function addressLabel(address: TypedAddress): string {
  return [...(address.addressLines ?? []), address.locality, address.administrativeArea, address.postalCode]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(', ');
}

/**
 * The Location check (piece 13). A building matches on its normalised typed
 * address, where the street words fold so St and Street, Ln and Lane, case and
 * punctuation all compare equal. 🔨 The match names why it matched and never
 * blocks the save.
 */
export function findLocationDuplicates(subject: LocationCandidate, candidates: readonly LocationCandidate[]): DuplicateMatch<LocationTier, LocationCandidate>[] {
  /**
   * 🔨 A place with no street line has no address to compare (decision 0673):
   * the country alone would read every field and yard as one building.
   */
  const streeted = (place: LocationCandidate) => (place.addressLines ?? []).some((line) => line.trim() !== '');
  const subjectAddress = streeted(subject) ? normaliseTypedAddress(subject) : '';
  const matches: DuplicateMatch<LocationTier, LocationCandidate>[] = [];
  for (const candidate of candidates) {
    if (candidate.id === subject.id || !streeted(candidate)) continue;
    if (subjectAddress && normaliseTypedAddress(candidate) === subjectAddress) {
      matches.push({ candidate, tier: 'address', reason: `same address once St, Street, case and punctuation are set aside: ${addressLabel(candidate)}` });
    }
  }
  return matches.sort((a, b) => LOCATION_TIERS.indexOf(a.tier) - LOCATION_TIERS.indexOf(b.tier));
}
